import * as React from 'react';
import { cn } from '@shared/utils/cn';
import { useOutputInspector } from '@/context/output-inspector';
import { useRunControl } from '@/context/run-control';
import { fetchSnapshot } from '@features/flows/runtime/snapshot';
import { redactDeep } from '@shared/utils/redact';
import { truncateJSON } from '@shared/utils/truncate';
import { JsonTree } from '@shared/output/JsonTree';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { X, GripVertical, MoveDiagonal2, HelpCircle } from 'lucide-react';
import { useRuntimeStore } from '@features/flows/runtime/useRuntimeStore';
import type { NodeTemplate } from '@features/flows/palette/registry';
import { downloadNodeIO, downloadNodeLogs } from '@features/flows/runtime/download';

function bestEffortExtract(snapshot: any, nodeId: string) {
  const bus = snapshot?.snapshot?.databus ?? snapshot?.databus ?? {};
  const node = bus.nodes?.[nodeId] ?? {};
  return {
    inputs: node.lastInputs ?? node.inputs ?? null,
    outputs: node.lastOutputs ?? node.outputs ?? null,
  } as { inputs?: any; outputs?: any };
}

type PinsSpec = {
  exec?: { in?: Array<{ id: string }>; out?: Array<{ id: string }> };
  data?: { in?: Array<{ id: string; type?: string; label?: string; required?: boolean }>; out?: Array<{ id: string; type?: string; label?: string; required?: boolean }> };
};

type DataPinWithSchema = { id: string; schema?: unknown; required?: boolean };

function deriveObjectSchemaFromPins(pins: DataPinWithSchema[]): Record<string, unknown> | undefined {
  if (!Array.isArray(pins) || pins.length === 0) return undefined;
  const properties: Record<string, unknown> = {};
  const required: string[] = [];
  let hasSchema = false;
  for (const pin of pins) {
    if (!pin?.id) continue;
    if (pin.schema && typeof pin.schema === 'object') {
      properties[pin.id] = pin.schema;
      hasSchema = true;
      if (pin.required) required.push(pin.id);
    }
  }
  if (!hasSchema) return undefined;
  const schema: Record<string, unknown> = { type: 'object', properties };
  if (required.length) schema.required = required;
  return schema;
}

function formatSchemaJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function SchemaHoverCard({ schema, title }: { schema: unknown; title: string }) {
  const content = React.useMemo(() => (schema == null ? null : formatSchemaJson(schema)), [schema]);
  if (!content) return null;
  return (
    <HoverCard openDelay={150}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          data-no-drag="true"
          className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          aria-label={`View schema for ${title}`}
          title={`View schema for ${title}`}
        >
          <HelpCircle className="h-3 w-3" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent className="z-[70] w-[520px] p-3" align="start">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{title} schema</div>
        <div className="max-h-[320px] max-w-full overflow-auto rounded-md border border-border/60">
          <pre className="min-w-full whitespace-pre-wrap break-words p-3 font-mono text-[11px] leading-relaxed">
            {content}
          </pre>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

export default function NodeInspectorPanel({
  mount = 'fixed',
  className,
  pinsByNodeId,
  templatesByNodeId,
}: {
  mount?: 'fixed' | 'container';
  className?: string;
  pinsByNodeId?: Record<string, PinsSpec>;
  templatesByNodeId?: Record<string, NodeTemplate | undefined>;
} = {}) {
  const { isOpen, pinnedNodeId, setOpen } = useOutputInspector();
  const { runId, flowId } = useRunControl();
  const runtime = useRuntimeStore((s) => (pinnedNodeId ? s.nodes[pinnedNodeId] : undefined));
  const [tab, setTab] = React.useState<'inputs' | 'outputs' | 'logs' | 'pins'>('outputs');
  const [activeIter, setActiveIter] = React.useState<number | null>(null);
  const [fallback, setFallback] = React.useState<{ inputs?: any; outputs?: any } | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [levelFilter, setLevelFilter] = React.useState<Set<'debug'|'info'|'warn'|'error'>>(
    () => new Set<'debug'|'info'|'warn'|'error'>(['debug','info','warn','error'])
  );

  React.useEffect(() => {
    if (!isOpen || !pinnedNodeId || runtime?.lastInputsPreview || runtime?.lastOutputsPreview || !runId) {
      setFallback(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchSnapshot(runId)
      .then((snap) => {
        if (cancelled) return;
        const ex = bestEffortExtract(snap, pinnedNodeId);
        setFallback(ex);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isOpen, pinnedNodeId, runId, runtime?.lastInputsPreview, runtime?.lastOutputsPreview]);

  const seenIters: number[] = React.useMemo(() => runtime?.seenIterations ?? [], [runtime?.seenIterations]);
  React.useEffect(() => {
    // Default to last seen iteration when pin changes or new iterations arrive
    if (!pinnedNodeId) { setActiveIter(null); return; }
    if (seenIters.length === 0) { setActiveIter(null); return; }
    setActiveIter((prev) => (prev == null || !seenIters.includes(prev)) ? seenIters[seenIters.length - 1] : prev);
  }, [pinnedNodeId, JSON.stringify(seenIters)]);

  const iterState = (activeIter != null && (runtime as any)?.iterations) ? (runtime as any).iterations[activeIter] : undefined;
  const inputs = (iterState?.inputsPreview ?? runtime?.lastInputsPreview ?? fallback?.inputs);
  const outputs = (iterState?.outputsPreview ?? runtime?.lastOutputsPreview ?? fallback?.outputs);
  const logs = (iterState?.logs ?? runtime?.logs ?? []);
  const canDownload = Boolean(runId);
  const filteredLogs = React.useMemo(() => (
    logs.filter((l) => levelFilter.has((l.level ?? 'info') as any))
  ), [logs, levelFilter]);

  // Defer early return until after all hooks are declared to keep hook order stable

  const toggleLevel = (lvl: 'debug'|'info'|'warn'|'error') => {
    setLevelFilter((prev) => {
      const next = new Set(prev);
      if (next.has(lvl)) next.delete(lvl);
      else next.add(lvl);
      return next;
    });
  };

  const renderJSON = (val: any) => {
    if (val == null) return <div className="text-xs text-muted-foreground">Not captured.</div>;
    const redacted = redactDeep(val);
    const { preview, truncated } = truncateJSON(redacted);
    if (!truncated && typeof redacted === 'object') {
      return (
        <div className="h-full overflow-auto border rounded">
          <JsonTree data={redacted} />
        </div>
      );
    }
    return (
      <div className="relative h-full">
        {truncated && (
          <div className="absolute right-2 top-1 text-[10px] rounded bg-amber-500/10 text-amber-600 px-1">truncated</div>
        )}
        <pre className="h-full overflow-auto text-[11px] border rounded p-2">{preview}</pre>
      </div>
    );
  };

  const [offset, setOffset] = React.useState<{ dx: number; dy: number }>({ dx: 0, dy: 0 });
  const dragActiveRef = React.useRef(false);
  const dragStartRef = React.useRef<{ x: number; y: number; dx0: number; dy0: number } | null>(null);

  const onDragStart = (e: React.PointerEvent) => {
    if (mount !== 'container') return; // only draggable in container overlay mode
    if ((e as any).button !== 0) return; // left button only
    // Prevent starting drag when interacting with elements marked as no-drag (e.g., close button)
    const t = e.target as HTMLElement | null;
    if (t && t.closest('[data-no-drag="true"]')) return;
    try { (e.currentTarget as any).setPointerCapture?.(e.pointerId); } catch {}
    dragActiveRef.current = true;
    dragStartRef.current = { x: e.clientX, y: e.clientY, dx0: offset.dx, dy0: offset.dy };
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', onDragEnd, { once: true });
  };

  const onDragMove = (ev: PointerEvent) => {
    if (!dragActiveRef.current || !dragStartRef.current) return;
    const { x, y, dx0, dy0 } = dragStartRef.current;
    let dx = dx0 + (ev.clientX - x);
    let dy = dy0 + (ev.clientY - y);
    // Constrain to container bounds
    const el = rootRef.current;
    const parentRect = el?.parentElement?.getBoundingClientRect();
    const panelRect = el?.getBoundingClientRect();
    const w = (width ?? panelRect?.width ?? 520);
    const h = (height ?? panelRect?.height ?? 360);
    if (parentRect && w && h) {
      const halfX = (parentRect.width - w) / 2;
      const halfY = (parentRect.height - h) / 2;
      if (halfX <= 0) dx = 0; else dx = Math.max(-halfX, Math.min(halfX, dx));
      if (halfY <= 0) dy = 0; else dy = Math.max(-halfY, Math.min(halfY, dy));
    }
    setOffset({ dx, dy });
  };

  const onDragEnd = () => {
    dragActiveRef.current = false;
    dragStartRef.current = null;
    window.removeEventListener('pointermove', onDragMove);
  };

  // Resizable: measurement and handlers (container mode). Hooks placed before early return.
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = React.useState<number | null>(null);
  const [height, setHeight] = React.useState<number | null>(null);
  const resizeStartRef = React.useRef<{ x: number; y: number; w0: number; h0: number; dx0: number; dy0: number } | null>(null);

  React.useEffect(() => {
    if (mount !== 'container') return;
    if (!isOpen) return;
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (width == null) setWidth(Math.max(520, Math.round(rect.width)));
    if (height == null) setHeight(Math.max(360, Math.round(rect.height)));
  }, [mount, isOpen, width, height]);

  const onResizeStart = (e: React.PointerEvent) => {
    if (mount !== 'container') return;
    if ((e as any).button !== 0) return;
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    try { (e.currentTarget as any).setPointerCapture?.(e.pointerId); } catch {}
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      w0: width ?? Math.round(rect.width),
      h0: height ?? Math.round(rect.height),
      dx0: offset.dx,
      dy0: offset.dy,
    };
    window.addEventListener('pointermove', onResizeMove);
    window.addEventListener('pointerup', onResizeEnd, { once: true });
  };

  const onResizeMove = (ev: PointerEvent) => {
    const st = resizeStartRef.current;
    if (!st) return;
    const minW = 480;
    const minH = 360;
    const dw = ev.clientX - st.x;
    const dh = ev.clientY - st.y;
    let newW = Math.max(minW, Math.round(st.w0 + dw));
    let newH = Math.max(minH, Math.round(st.h0 + dh));
    const el = rootRef.current;
    const parentRect = el?.parentElement?.getBoundingClientRect();
    if (parentRect) {
      newW = Math.min(newW, Math.round(parentRect.width));
      newH = Math.min(newH, Math.round(parentRect.height));
    }
    setWidth(newW);
    setHeight(newH);
    // keep top-left anchored while resizing by offsetting half the delta
    let ndx = st.dx0 + Math.round((newW - st.w0) / 2);
    let ndy = st.dy0 + Math.round((newH - st.h0) / 2);
    if (parentRect) {
      const halfX = (parentRect.width - newW) / 2;
      const halfY = (parentRect.height - newH) / 2;
      if (halfX <= 0) ndx = 0; else ndx = Math.max(-halfX, Math.min(halfX, ndx));
      if (halfY <= 0) ndy = 0; else ndy = Math.max(-halfY, Math.min(halfY, ndy));
    }
    setOffset({ dx: ndx, dy: ndy });
  };

  const onResizeEnd = () => {
    resizeStartRef.current = null;
    window.removeEventListener('pointermove', onResizeMove);
  };

  if (!isOpen || !pinnedNodeId) return null;

  const rootClass = cn(
    mount === 'fixed' ? 'fixed right-2 bottom-2' : 'absolute left-1/2 top-1/2',
    'z-50 w-[520px] max-h-[80vh] rounded-md border bg-background shadow-lg flex flex-col',
    className,
  );

  const levelClass = (lvl: string | undefined) => {
    switch (lvl) {
      case 'error':
        return 'border-red-500/30 bg-red-500/10 text-red-600';
      case 'warn':
        return 'border-amber-500/30 bg-amber-500/10 text-amber-600';
      case 'debug':
        return 'border-slate-500/30 bg-slate-500/10 text-slate-400';
      case 'info':
      default:
        return 'border-blue-500/30 bg-blue-500/10 text-blue-600';
    }
  };

  // Compose transform for container mode so it starts centered and moves by offset
  const style = mount === 'container'
    ? ({
        transform: `translate(calc(-50% + ${offset.dx}px), calc(-50% + ${offset.dy}px))`,
        width: width ?? undefined,
        height: height ?? undefined,
      } as React.CSSProperties)
    : undefined;

  return (
    <div ref={rootRef} className={rootClass} style={style} data-inspector-root>
      <div
        className={cn('h-9 px-3 flex items-center justify-between border-b select-none', mount === 'container' ? 'cursor-move' : 'cursor-default')}
        onPointerDown={onDragStart}
        title={mount === 'container' ? 'Drag to move' : undefined}
        aria-label="Inspector header"
      >
        <div className="text-sm font-medium truncate inline-flex items-center gap-2">
          <GripVertical className={cn('h-4 w-4', mount === 'container' ? 'opacity-80' : 'opacity-40')} aria-hidden />
          <span>
            Inspector — <span className="font-mono">{pinnedNodeId}</span>
          </span>
        </div>
        <Button data-no-drag="true" variant="ghost" size="icon" onClick={() => setOpen(false)} aria-label="Close inspector">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="p-2 flex-1 min-h-0 flex flex-col">
        <div className="flex items-center gap-2 mb-2">
          <button
            className={`text-xs px-2 py-1 rounded border ${tab === 'inputs' ? 'bg-accent' : ''}`}
            onClick={() => setTab('inputs')}
          >
            Inputs
          </button>
          <button
            className={`text-xs px-2 py-1 rounded border ${tab === 'outputs' ? 'bg-accent' : ''}`}
            onClick={() => setTab('outputs')}
          >
            Outputs
          </button>
          <button
            className={`text-xs px-2 py-1 rounded border ${tab === 'logs' ? 'bg-accent' : ''}`}
            onClick={() => setTab('logs')}
          >
            Logs
          </button>
          <button
            className={`text-xs px-2 py-1 rounded border ${tab === 'pins' ? 'bg-accent' : ''}`}
            onClick={() => setTab('pins')}
          >
            Pins
          </button>
          <IterationSelector
            iterations={seenIters}
            active={activeIter}
            onChange={setActiveIter}
          />
        </div>
        <Separator className="my-2" />
        {tab === 'pins' && (
          <div className="flex-1 min-h-0 overflow-auto">
            {(() => {
              const pins = pinnedNodeId ? pinsByNodeId?.[pinnedNodeId] : undefined;
              const tpl = pinnedNodeId ? templatesByNodeId?.[pinnedNodeId] : undefined;
              if (!pins && !tpl) return <div className="text-xs text-muted-foreground">No pin info available for this node.</div>;

              const coarseToVar = (t?: string) => {
                switch (String(t || 'any')) {
                  case 'string': return 'var(--type-string)';
                  case 'number': return 'var(--type-number)';
                  case 'boolean': return 'var(--type-boolean)';
                  case 'object': return 'var(--type-object)';
                  case 'array': return 'var(--type-array)';
                  default: return 'var(--type-any)';
                }
              };

              const ExecDot = () => (
                <span className="inline-block relative w-3 h-3 rounded-full" style={{ background: 'var(--exec-bg)' }}>
                  <span className="absolute inset-[3px] rounded-full" style={{ background: 'var(--exec-dot)' }} />
                </span>
              );
              const DataDot = ({ coarse }: { coarse?: string }) => (
                <span
                  className="inline-block w-3 h-3 rounded-full border-2"
                  style={{ borderColor: coarseToVar(coarse) }}
                />
              );

              const reqIn = new Set<string>(Array.isArray((tpl as any)?.inputSchema?.required) ? ((tpl as any).inputSchema.required as string[]) : []);
              const inProps = ((tpl as any)?.inputSchema?.properties ?? {}) as Record<string, any>;
              const outProps = ((tpl as any)?.outputSchema?.properties ?? {}) as Record<string, any>;

              // Derive typed lists, preferring explicit pins prop, else template ports
              const execIn = pins?.exec?.in ?? (tpl ? tpl.ports.exec.in.map(p => ({ id: p.id })) : []);
              const execOut = pins?.exec?.out ?? (tpl ? tpl.ports.exec.out.map(p => ({ id: p.id })) : []);
              const dataIn = (pins?.data?.in ?? (tpl ? tpl.ports.data.in.map(p => ({ id: p.id, type: p.type })) : []))
                .map((p) => ({ ...p, type: (p as any).type || (tpl?.ports.data.in.find(x => x.id === p.id)?.type), required: reqIn.has(p.id), schema: inProps[p.id] }));
              const dataOut = (pins?.data?.out ?? (tpl ? tpl.ports.data.out.map(p => ({ id: p.id, type: p.type })) : []))
                .map((p) => ({ ...p, type: (p as any).type || (tpl?.ports.data.out.find(x => x.id === p.id)?.type), schema: outProps[p.id] }));

              const inputSchema = (tpl as any)?.inputSchema ?? deriveObjectSchemaFromPins(dataIn as DataPinWithSchema[]);
              const outputSchema = (tpl as any)?.outputSchema ?? deriveObjectSchemaFromPins(dataOut as DataPinWithSchema[]);

              const PinRow = ({ left, right }: { left: React.ReactNode; right?: React.ReactNode }) => (
                <li className="text-[11px] rounded border px-2 py-1 flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-2 min-w-0">
                    {left}
                  </span>
                  {right ? <span className="shrink-0 text-[10px] text-muted-foreground">{right}</span> : null}
                </li>
              );

              const TypeBadge = ({ coarse }: { coarse?: string }) => (
                <span className="inline-flex items-center gap-1 text-[10px]">
                  <DataDot coarse={coarse} />
                  <span className="capitalize">{String(coarse || 'any')}</span>
                </span>
              );

              return (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs font-semibold mb-1">Exec — In</div>
                    {execIn.length === 0 ? (
                      <div className="text-xs text-muted-foreground">None</div>
                    ) : (
                      <ul className="space-y-1">
                        {execIn.map((p, i) => (
                          <PinRow key={`ein-${i}`} left={<><ExecDot /> <span className="font-mono truncate">{p.id}</span></>} />
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <div className="text-xs font-semibold mb-1">Exec — Out</div>
                    {execOut.length === 0 ? (
                      <div className="text-xs text-muted-foreground">None</div>
                    ) : (
                      <ul className="space-y-1">
                        {execOut.map((p, i) => (
                          <PinRow key={`eout-${i}`} left={<><span className="font-mono truncate">{p.id}</span> <ExecDot /></>} />
                        ))}
                      </ul>
                    )}
                  </div>

                  <div>
                    <div className="mb-1 flex items-center gap-1 text-xs font-semibold">
                      <span>Data — In</span>
                      <SchemaHoverCard schema={inputSchema} title="Data — In" />
                    </div>
                    {dataIn.length === 0 ? (
                      <div className="text-xs text-muted-foreground">None</div>
                    ) : (
                      <ul className="space-y-1">
                        {dataIn.map((p, i) => (
                          <PinRow
                            key={`din-${i}`}
                            left={<><DataDot coarse={String((p as any).type || 'any')} /> <span className="font-mono truncate">{p.id}</span></>}
                            right={<>
                              <TypeBadge coarse={String((p as any).type || 'any')} />
                              {p.required ? <span className="ml-2 text-[10px] text-amber-600">required</span> : null}
                            </>}
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <div className="mb-1 flex items-center gap-1 text-xs font-semibold">
                      <span>Data — Out</span>
                      <SchemaHoverCard schema={outputSchema} title="Data — Out" />
                    </div>
                    {dataOut.length === 0 ? (
                      <div className="text-xs text-muted-foreground">None</div>
                    ) : (
                      <ul className="space-y-1">
                        {dataOut.map((p, i) => (
                          <PinRow
                            key={`dout-${i}`}
                            left={<><span className="font-mono truncate">{p.id}</span></>}
                            right={<TypeBadge coarse={String((p as any).type || 'any')} />}
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
        {tab === 'inputs' && (
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex justify-between items-center mb-2 shrink-0">
              <div className="text-xs text-muted-foreground">Latest inputs preview</div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => canDownload && flowId && pinnedNodeId && runId && downloadNodeIO({ flowId, nodeId: pinnedNodeId, runId, kind: 'inputs', iteration: activeIter ?? undefined })}
                disabled={!canDownload || !flowId || (seenIters.length > 0 && activeIter == null)}
                title={canDownload ? `Download inputs${activeIter != null ? ` (iter ${activeIter})` : ''}` : 'No run attached'}
              >
                Download full
              </Button>
            </div>
            {loading && <div className="text-xs text-muted-foreground">Loading snapshot…</div>}
            <div className="flex-1 min-h-0 overflow-hidden">
              {renderJSON(inputs)}
            </div>
          </div>
        )}
        {tab === 'outputs' && (
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex justify-between items-center mb-2 shrink-0">
              <div className="text-xs text-muted-foreground">Latest output preview</div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => canDownload && flowId && pinnedNodeId && runId && downloadNodeIO({ flowId, nodeId: pinnedNodeId, runId, kind: 'outputs', iteration: activeIter ?? undefined })}
                disabled={!canDownload || !flowId || (seenIters.length > 0 && activeIter == null)}
                title={canDownload ? `Download outputs${activeIter != null ? ` (iter ${activeIter})` : ''}` : 'No run attached'}
              >
                Download full
              </Button>
            </div>
            {loading && <div className="text-xs text-muted-foreground">Loading snapshot…</div>}
            <div className="flex-1 min-h-0 overflow-hidden">
              {renderJSON(outputs)}
            </div>
          </div>
        )}
        {tab === 'logs' && (
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex justify-between items-center mb-2 shrink-0">
              <div className="flex items-center gap-2">
                <div className="text-xs text-muted-foreground">Runtime logs {activeIter != null && (<span className="ml-1 text-[10px] px-1 rounded border">iter {activeIter}</span>)}</div>
                <div className="flex items-center gap-1">
                  {(['debug','info','warn','error'] as const).map((lvl) => (
                    <button
                      key={lvl}
                      className={cn(
                        'text-[10px] px-1.5 py-0.5 rounded border capitalize',
                        levelFilter.has(lvl) ? 'bg-accent' : 'opacity-60'
                      )}
                      onClick={() => toggleLevel(lvl)}
                      title={`Toggle ${lvl}`}
                    >
                      {lvl}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => canDownload && flowId && pinnedNodeId && runId && downloadNodeLogs({ flowId, nodeId: pinnedNodeId, runId, iteration: activeIter ?? undefined })}
                  disabled={!canDownload || logs.length === 0 || !flowId || (seenIters.length > 0 && activeIter == null)}
                >
                  Download logs
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigator.clipboard?.writeText(filteredLogs.map(l => `[${new Date(l.t).toISOString()}] ${String(l.level).toUpperCase()} ${l.msg}`).join('\n'))}
                  disabled={filteredLogs.length === 0}
                >
                  Copy visible
                </Button>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              {filteredLogs.length === 0 ? (
                <div className="text-xs text-muted-foreground">No logs.</div>
              ) : (
                <ul className="space-y-1">
                  {filteredLogs.map((l, i) => (
                    <li key={i} className={cn('text-[11px] font-mono rounded border px-2 py-1', levelClass(l.level))}>
                      <span className="text-muted-foreground">{new Date(l.t).toLocaleTimeString()}</span>{' '}
                      <span className={cn('uppercase tracking-wide text-[10px] font-semibold')}>[{l.level}]</span>{' '}
                      <span>{l.msg}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
      {mount === 'container' && (
        <button
          type="button"
          aria-label="Resize"
          title="Drag to resize"
          onPointerDown={onResizeStart}
          className="absolute bottom-1 right-1 inline-flex items-center justify-center w-4 h-4 rounded text-muted-foreground hover:text-foreground cursor-nwse-resize"
        >
          <MoveDiagonal2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// Adaptive iteration selector with windowed pills, prev/next, numeric input
function IterationSelector({ iterations, active, onChange }: { iterations: number[]; active: number | null; onChange: (n: number | null) => void }) {
  const hasIters = iterations && iterations.length > 0;
  const last = hasIters ? iterations[iterations.length - 1] : null;
  const first = hasIters ? iterations[0] : null;
  const activeVal = active != null ? active : last;

  const goPrev = () => {
    if (!hasIters || activeVal == null) return;
    const idx = iterations.indexOf(activeVal);
    if (idx > 0) onChange(iterations[idx - 1]);
  };
  const goNext = () => {
    if (!hasIters || activeVal == null) return;
    const idx = iterations.indexOf(activeVal);
    if (idx >= 0 && idx < iterations.length - 1) onChange(iterations[idx + 1]);
  };
  const goLatest = () => { if (last != null) onChange(last); };

  const setFromInput = (raw: string) => {
    if (!hasIters) return;
    const val = Number(raw);
    if (Number.isNaN(val)) return;
    // snap to nearest existing iteration
    let nearest = iterations[0];
    let best = Math.abs(val - nearest);
    for (let i = 1; i < iterations.length; i++) {
      const d = Math.abs(val - iterations[i]);
      if (d < best) { best = d; nearest = iterations[i]; }
    }
    onChange(nearest);
  };

  // Build windowed set for pills when count is reasonable
  const WINDOW = 2;
  const MAX_PILLS = 12;
  const usePills = (iterations?.length || 0) <= MAX_PILLS;
  const pillList = React.useMemo(() => {
    if (!hasIters) return [] as Array<number | 'ellipsis'>;
    if (usePills) return iterations as Array<number | 'ellipsis'>;
    const set = new Set<number>();
    if (first != null) { set.add(first); if (iterations.length > 1) set.add(iterations[1]); }
    if (last != null) { set.add(last); if (iterations.length > 1) set.add(iterations[iterations.length - 2]); }
    if (activeVal != null) {
      for (let v = activeVal - WINDOW; v <= activeVal + WINDOW; v++) {
        if (iterations.includes(v)) set.add(v);
      }
    }
    const arr = Array.from(set).sort((a, b) => a - b);
    // Insert ellipses where gaps exist
    const out: Array<number | 'ellipsis'> = [];
    for (let i = 0; i < arr.length; i++) {
      out.push(arr[i]);
      if (i < arr.length - 1 && arr[i + 1] !== arr[i] + 1) out.push('ellipsis');
    }
    return out;
  }, [hasIters, usePills, iterations, first, last, activeVal]);

  return (
    <div className="ml-auto w-[480px] max-w-full">
      {!hasIters ? (
        <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground">Iteration:</span><span className="text-xs text-muted-foreground">—</span></div>
      ) : (
        <div className="flex flex-col gap-1 w-full">
          <div className="flex items-center justify-between gap-2 w-full">
            <div className="inline-flex items-center gap-2 shrink-0">
              <span className="text-xs text-muted-foreground">Iteration:</span>
              <div className="inline-flex items-center gap-1">
                <button className="text-[10px] px-1.5 py-0.5 rounded border" onClick={goPrev} title="Previous" disabled={activeVal == null || activeVal === first}>◀</button>
                <button className="text-[10px] px-1.5 py-0.5 rounded border" onClick={goNext} title="Next" disabled={activeVal == null || activeVal === last}>▶</button>
                <button className="text-[10px] px-1.5 py-0.5 rounded border" onClick={goLatest} title="Latest" disabled={activeVal == null || activeVal === last}>Latest</button>
              </div>
            </div>
            <div className="inline-flex items-center gap-1 shrink-0">
              <input
                type="number"
                className="w-16 text-[11px] border rounded px-1 py-0.5"
                min={first ?? undefined}
                max={last ?? undefined}
                value={activeVal ?? ''}
                onChange={(e) => setFromInput(e.target.value)}
                title={first != null && last != null ? `Range: ${first}..${last}` : 'Set iteration'}
              />
              <span className="text-[10px] text-muted-foreground">of {last}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-1">
            {pillList.map((v, idx) => (
              v === 'ellipsis' ? (
                <span key={`e${idx}`} className="inline-flex items-center justify-center w-10 text-xs text-muted-foreground">…</span>
              ) : (
                <button
                  key={v}
                  className={cn(
                    'inline-flex items-center justify-center w-10 text-[10px] px-1.5 py-0.5 rounded border',
                    v === activeVal ? 'bg-accent' : 'hover:bg-muted'
                  )}
                  onClick={() => onChange(v)}
                  title={`Show iteration ${v}`}
                >
                  {v}
                </button>
              )
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
